/* ==========================================================================
   Som do cliente esperando atendimento (change customer-agent-handoff, D19)

   Diferente de propósito dos outros dois sons do painel, para quem está no
   balcão distinguir sem olhar a tela:
   - pedido novo (`playOrderNotification`): dois tons senoidais subindo, C5 → E5;
   - cupom sem confirmação (`playPrintingAlert`): três bipes quadrados em G4;
   - atendimento (este): dois tons descendentes em onda triangular, E5 → C5,
     duas vezes.

   O navegador só deixa tocar áudio depois de um gesto na aba. O
   `AudioContext` é criado uma vez, no primeiro `pointerdown` ou `keydown`
   (`installHandoffAudioUnlock`), e reaproveitado; sem ele, `playHandoffAlert`
   não toca e devolve falso, e a faixa oferece "Ativar som". Tudo em
   `try/catch`: navegador sem Web Audio não quebra a faixa.

   O Safari e o iOS suspendem o contexto depois de um tempo parado ou de uma
   interrupção de áudio (uma ligação, outro app tocando). O contexto que já
   tocou e foi suspenso conta como áudio não liberado: a faixa volta a mostrar
   "Ativar som", o próximo gesto na aba o retoma, e `playHandoffAlert` tenta
   retomá-lo antes de tocar.
   ========================================================================== */

export interface HandoffTone {
  /** Hz. */
  frequency: number;
  /** Segundos desde o início do som. */
  offset: number;
  /** Segundos. */
  duration: number;
}

export const HANDOFF_ALERT_WAVE: OscillatorType = 'triangle';

const E5 = 659.25;
const C5 = 523.25;
const TONE_SECONDS = 0.18;

/** E5 → C5, uma pausa curta, e de novo E5 → C5. */
export const HANDOFF_ALERT_TONES: readonly HandoffTone[] = [
  { frequency: E5, offset: 0, duration: TONE_SECONDS },
  { frequency: C5, offset: 0.2, duration: TONE_SECONDS },
  { frequency: E5, offset: 0.55, duration: TONE_SECONDS },
  { frequency: C5, offset: 0.75, duration: TONE_SECONDS },
];

const PEAK_GAIN = 0.25;

type AudioContextConstructor = new () => AudioContext;

let context: AudioContext | null = null;
/** O contexto chegou a rodar: só o suspenso depois disso conta como bloqueado. */
let wasRunning = false;
let removeResumeOnGesture: (() => void) | null = null;
const unlockListeners = new Set<(unlocked: boolean) => void>();

function isSuspended(ctx: AudioContext): boolean {
  const state = ctx.state as string;
  return state === 'suspended' || state === 'interrupted';
}

function tryResume(ctx: AudioContext): void {
  try {
    if (isSuspended(ctx)) void ctx.resume().catch(() => undefined);
  } catch {
    // Sem como retomar agora: o próximo gesto tenta de novo.
  }
}

function notifyUnlock(unlocked: boolean): void {
  for (const listener of unlockListeners) listener(unlocked);
}

/** O próximo gesto na aba retoma o contexto suspenso (o navegador só deixa retomar num gesto). */
function armResumeOnGesture(ctx: AudioContext): void {
  if (removeResumeOnGesture || typeof window === 'undefined') return;
  const events = ['pointerdown', 'keydown'] as const;
  const handler = () => {
    tryResume(ctx);
    remove();
  };
  const remove = () => {
    for (const event of events) window.removeEventListener(event, handler, true);
    removeResumeOnGesture = null;
  };
  for (const event of events) window.addEventListener(event, handler, true);
  removeResumeOnGesture = remove;
}

/** Acompanha o estado do contexto: suspenso depois de rodar, o áudio volta a pedir um gesto. */
function watchState(ctx: AudioContext): void {
  if (ctx.state === 'running') wasRunning = true;
  try {
    ctx.addEventListener?.('statechange', () => {
      if (context !== ctx) return;
      if (ctx.state === 'running') {
        const resumed = wasRunning;
        wasRunning = true;
        removeResumeOnGesture?.();
        if (resumed) notifyUnlock(true);
      } else if (isSuspended(ctx) && wasRunning) {
        armResumeOnGesture(ctx);
        notifyUnlock(false);
      }
    });
  } catch {
    // Contexto sem eventos: fica com o estado da criação.
  }
}

function audioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === 'undefined') return null;
  const candidate = (window as unknown as { AudioContext?: AudioContextConstructor; webkitAudioContext?: AudioContextConstructor })
    .AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
  return candidate ?? null;
}

/** A aba já liberou o áudio (houve gesto e o `AudioContext` existe) e o navegador não o suspendeu depois de tocar. */
export function isHandoffAudioUnlocked(): boolean {
  return context !== null && !(wasRunning && isSuspended(context));
}

/** Avisa quando o áudio da aba é liberado (para o heartbeat da aba líder). */
export function onHandoffAudioUnlock(listener: (unlocked: boolean) => void): () => void {
  unlockListeners.add(listener);
  return () => unlockListeners.delete(listener);
}

/**
 * Cria o `AudioContext` da aba. Chamado dentro de um gesto (o primeiro
 * clique ou tecla, ou o "Ativar som"); devolve se o áudio ficou liberado.
 */
export function unlockHandoffAudio(): boolean {
  if (context) {
    tryResume(context);
    return true;
  }
  const Constructor = audioContextConstructor();
  if (!Constructor) return false;
  try {
    context = new Constructor();
    watchState(context);
    tryResume(context);
  } catch {
    context = null;
    return false;
  }
  notifyUnlock(true);
  return true;
}

/**
 * Libera o áudio no primeiro gesto da aba (`pointerdown` ou `keydown`). Os
 * ouvintes saem sozinhos depois da liberação; a função devolvida os tira
 * antes, quando a faixa desmonta.
 */
export function installHandoffAudioUnlock(target: Pick<Window, 'addEventListener' | 'removeEventListener'> = window): () => void {
  if (isHandoffAudioUnlocked()) return () => undefined;
  const events = ['pointerdown', 'keydown'] as const;
  const handler = () => {
    if (unlockHandoffAudio()) remove();
  };
  const remove = () => {
    for (const event of events) target.removeEventListener(event, handler, true);
  };
  for (const event of events) target.addEventListener(event, handler, true);
  return remove;
}

/** Toca o som do atendimento; falso quando a aba ainda não liberou o áudio ou o navegador não tem Web Audio. */
export function playHandoffAlert(): boolean {
  if (!context) return false;
  // Suspenso pelo navegador: tenta retomar; os tons ficam agendados e tocam se ele voltar.
  tryResume(context);
  try {
    const now = context.currentTime;
    for (const tone of HANDOFF_ALERT_TONES) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = HANDOFF_ALERT_WAVE;
      oscillator.frequency.value = tone.frequency;
      const start = now + tone.offset;
      const end = start + tone.duration;
      gain.gain.setValueAtTime(PEAK_GAIN, start);
      gain.gain.exponentialRampToValueAtTime(0.01, end);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(end);
    }
    return true;
  } catch {
    // Navegador sem áudio: a faixa e o título continuam.
    return false;
  }
}

/** Só para teste: esquece o `AudioContext` e os ouvintes da aba. */
export function resetHandoffAlertSound(): void {
  try {
    void context?.close();
  } catch {
    // Contexto falso dos testes.
  }
  context = null;
  wasRunning = false;
  removeResumeOnGesture?.();
  unlockListeners.clear();
}
